import { Component } from '@nicolas-riciotti/light2';

const CHARTS = {
  'health-intents-percent-of-conversations': () =>
import('../../charts/health-intents-percent-of-conversations.js'),
  'health-intents-mobile': () =>
import('../../charts/health-intents-mobile.js'),
  'health-intent-patterns-increasing': () =>
import('../../charts/health-intent-patterns-increasing.js'),
  'health-intent-patterns-decreasing': () =>
import('../../charts/health-intent-patterns-decreasing.js'),
  'symptom-questions-who-are-users-asking-for': () =>
import('../../charts/symptom-questions-who-are-users-asking-for.js'),
  'transcribe-wer-overall': () =>
import('../../charts/transcribe-wer-overall.js'),
  'transcribe-wer-by-locale': () =>
import('../../charts/transcribe-wer-by-locale.js'),
  'mai-image-2e-benchmark': () =>
import('../../charts/mai-image-2e-benchmark.js'),
  'mai-image-2-5-categories': () =>
import('../../charts/mai-image-2-5-categories.js'),
  'languages-beating-models': () =>
import('../../charts/languages-beating-models.js'),
  'transcribe-wer-by-model': () =>
import('../../charts/transcribe-wer-by-model.js'),
  'transcribe-wer-language-table': () =>
import('../../charts/transcribe-wer-language-table.js'),
  'reasoning-effort': () =>
import('../../charts/reasoning-effort.js'),
  'outperforming-benchmarks': () =>
import('../../charts/outperforming-benchmarks.js'),
  'pass-rate-vs-tokens': () =>
import('../../charts/pass-rate-vs-tokens.js'),
  'benchmark-scores': () =>
import('../../charts/benchmark-scores.js'),
  'benchmark-stat-cards': () =>
import('../../charts/benchmark-stat-cards.js'),
  'safety-model-advancement': () =>
import('../../charts/safety-model-advancement.js'),
  'mai-image-elo-radar': () =>
import('../../charts/mai-image-elo-radar.js'),
};

export default class BlockChart extends Component {
  attached() {
    const container = this.querySelector('[data-chart-container]');
    if (!container) return;

    const CHART_KEY = this.getAttribute('data-chart-name');
    // Drop-in charts: a raw ES module URL served from the theme /charts
    // folder. Takes precedence over the bundled CHARTS map so new charts
    // (or hotfixes of bundled ones) work without a rebuild.
    const CHART_URL = this.getAttribute('data-chart-url');
    const title = this.getAttribute('data-chart-title');
    const subtitle = this.getAttribute('data-chart-subtitle');

    let json;
    if (this.hasAttribute('data-chart-json')) {
      json = JSON.parse( this.getAttribute('data-chart-json') );
      console.log('THE_JSON ?', json);
    }


    const loadChart = CHART_URL
      ? () => import(/* @vite-ignore */ CHART_URL)
      : CHARTS[CHART_KEY];
    if (!loadChart) return;

    let d3;
    let utils;
    let render;

    let scrollTimer;
    let preBuilt = false;
    let rendered = false;
    let resizeObserver;
    let lastWidth;
    let renderTimer = null;
    let chartPlay = null;
    let loadingPromise = null;

    let stopped = localStorage['accessibility'] === 'true';

    async function loadDeps() {
      if (loadingPromise) return loadingPromise;

      loadingPromise = Promise.all([
        import('d3'),
        import('../../charts/utils.js'),
        loadChart(),
      ]).then(([d3Module, utilsModule, chartModule]) => {
        d3 = d3Module;
        utils = utilsModule;
        render = chartModule.render;
      });

      return loadingPromise;
    }

    async function _doRender(playIntro) {
      await loadDeps();

      if (!render) return;

      container.innerHTML = '';

      const width = container.clientWidth;
      container.style.overflow = 'visible';

      chartPlay = render({
        title,
        subtitle,
        json,
        container,
        width,
        d3,
        playIntro,
        animate: !stopped,
        utils,
      });

      container.querySelectorAll('svg').forEach((svg) => {
        svg.style.overflow = 'visible';
      });

      container.querySelectorAll('svg text').forEach((el) => {
        // Charts can opt subtrees out of the mono override by adding a
        // .keep-font class to an ancestor <g> (used when labels should
        // render in the site's brand serif instead).
        if (el.closest('.keep-font')) return;
        el.setAttribute('font-family', "'Red Hat Mono', monospace");
      });
    }

    function _render(playIntro) {
      clearTimeout(renderTimer);

      renderTimer = setTimeout(async () => {
        await _doRender(playIntro);
        if (chartPlay) chartPlay();
      }, 100);
    }

    function siteSettingsMotionChange(event) {
      stopped = event.detail === 'off';
      _render(!stopped);
    }

    document.addEventListener('site_settings_motion', siteSettingsMotionChange, false);

    const preObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !preBuilt) {
        preBuilt = true;
        preObserver.disconnect();
        _doRender(true);
      }
    }, {
      threshold: 0.01,
      rootMargin: '0px 0px 300px 0px',
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(async (entry) => {
        if (entry.isIntersecting && !rendered) {
          rendered = true;
          observer.disconnect();

          if (!chartPlay) await _doRender(true);

          container.style.visibility = '';

          if (chartPlay) chartPlay();

          lastWidth = container.clientWidth;

          resizeObserver = new ResizeObserver(() => {
            const newWidth = container.clientWidth;

            if (newWidth !== lastWidth) {
              lastWidth = newWidth;
              _render(false);
            }
          });

          resizeObserver.observe(container);

          window.addEventListener('scroll', () => {
            container.style.pointerEvents = 'none';

            clearTimeout(scrollTimer);

            scrollTimer = setTimeout(() => {
              container.style.pointerEvents = '';
            }, 500);
          }, { passive: true });
        }
      });
    }, {
      threshold: 0.15,
    });

    preObserver.observe(container);
    observer.observe(container);
  }
}

customElements.define('block-chart', BlockChart);